import React from 'react';
import { FiClock, FiRepeat, FiCheckCircle, FiXCircle, FiAlertTriangle, FiFileText } from 'react-icons/fi';
import { MdPayments } from 'react-icons/md';
import type { RecentPayment } from '../types';

interface RecentPaymentsSectionProps {
  payments: RecentPayment[];
  onRepeat: (payment: RecentPayment) => void;
  onViewReceipt?: (payment: RecentPayment) => void;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-CV', {
    style: 'decimal',
    minimumFractionDigits: 0,
  }).format(value) + '$';
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `Há ${diffDays} dias`;

  return date.toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short' });
};

const StatusIcon: React.FC<{ status: RecentPayment['status'] }> = ({ status }) => {
  switch (status) {
    case 'success':
      return <FiCheckCircle className="text-green-500" />;
    case 'failed':
      return <FiXCircle className="text-red-500" />;
    case 'pending':
      return <FiAlertTriangle className="text-yellow-500" />;
  }
};

const RecentPaymentsSection: React.FC<RecentPaymentsSectionProps> = ({
  payments,
  onRepeat,
  onViewReceipt,
}) => {
  const successPayments = payments.filter(p => p.status === 'success');

  if (successPayments.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-pagali-gray-100 p-4">
      <div className="flex items-center gap-2 mb-4">
        <FiClock className="text-pagali-primary" />
        <h3 className="font-semibold text-pagali-gray-800">Pagamentos Recentes</h3>
      </div>

      <div className="space-y-2">
        {successPayments.slice(0, 5).map((payment) => (
          <div
            key={payment.id}
            onClick={() => onViewReceipt?.(payment)}
            className={`flex items-center gap-3 p-3 bg-pagali-gray-50 rounded-xl transition-colors ${onViewReceipt ? 'cursor-pointer hover:bg-pagali-gray-100' : ''}`}
          >
            {payment.serviceLogo ? (
              <img
                src={`data:${payment.serviceMimeType || 'image/png'};base64,${payment.serviceLogo}`}
                alt=""
                className="w-10 h-10 rounded-full object-contain bg-white p-1 ring-2 ring-pagali-gray-100"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center ring-2 ring-pagali-gray-100">
                <MdPayments className="text-pagali-gray-400" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-pagali-gray-800 truncate">
                  {payment.serviceName}
                </p>
                <StatusIcon status={payment.status} />
              </div>
              <p className="text-sm text-pagali-gray-500">
                Ref: {payment.referencia} &bull; {formatDate(payment.paidAt)}
              </p>
            </div>

            <div className="text-right flex-shrink-0">
              <p className="font-semibold text-pagali-gray-800">
                {formatCurrency(payment.montante)}
              </p>
              {payment.status === 'success' && (
                <div className="flex items-center justify-end gap-3 mt-1">
                  {onViewReceipt && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onViewReceipt(payment); }}
                      className="text-xs text-pagali-gray-500 hover:text-pagali-primary flex items-center gap-1"
                      title="Ver recibo"
                    >
                      <FiFileText className="text-sm" />
                      Recibo
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); onRepeat(payment); }}
                    className="text-xs text-pagali-primary hover:text-pagali-primary/80 flex items-center gap-1"
                  >
                    <FiRepeat className="text-sm" />
                    Repetir
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentPaymentsSection;
